import { DeleteFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Form, Input, Row, InputNumber, Select } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const { Option } = Select;

const ProductsFormList = ({ inputFields, setInputFields, data, categoriesListData }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const handleFormChange = (index, field, value) => {
    const newFields = [...inputFields];
    newFields[index][field] = value;
    setInputFields(newFields);
  };

  const handleOptionChange = (productIndex, optionIndex, field, value) => {
    const newFields = [...inputFields];
    if (!newFields[productIndex].options) newFields[productIndex].options = [];
    newFields[productIndex].options[optionIndex][field] = value;
    setInputFields(newFields);
  };

  const addOption = (productIndex) => {
    const newFields = [...inputFields];

    const existingOptions = newFields[productIndex].options || [];
    const clonedOptions = [...existingOptions];

    clonedOptions.push({
      valueEn: "",
      valueAr: "",
      availableQuantity: 0,
    });

    newFields[productIndex] = {
      ...newFields[productIndex],
      options: clonedOptions,
    };

    setInputFields(newFields);
  };

  const removeOption = (productIndex, optionIndex) => {
    const newFields = [...inputFields];
    newFields[productIndex].options.splice(optionIndex, 1);
    setInputFields(newFields);
  };

  const addFields = () => {
    setInputFields([
      ...inputFields,
      {
        nameEn: "",
        nameAr: "",
        descriptionEn: "",
        descriptionAr: "",
        picture: null,
        price: null,
        active: false,
        optionCategory: "",
        options: [
          {
            valueEn: "",
            valueAr: "",
            availableQuantity: 0,
          },
        ],
      },
    ]);
  };

  const removeFields = (index) => {
    const newFields = [...inputFields];
    newFields.splice(index, 1);
    setInputFields(newFields);
  };

  return (
    <Form.List name="productsForm" initialValue={inputFields}>
      {(fields, { add, remove }) =>
        fields.map((field, index) => (
          <React.Fragment key={field.key}>
            <Card
              title={`${t("dashboard.landingPageSettings.productsSection.cardTitle")} ${index + 1}`}
              extra={
                "id" in inputFields[index] ? null : (
                  <DeleteFilled
                    onClick={() => {
                      removeFields(index);
                      remove(field.name);
                    }}
                  />
                )
              }
              className="mb-4"
            >
              <Row gutter={[8, 8]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.nameEn")}
                    name={[index, "nameEn"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <Input
                      onChange={(e) => handleFormChange(index, "nameEn", e.target.value)}
                      className="english-field"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.nameAr")}
                    name={[index, "nameAr"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <Input
                      onChange={(e) => handleFormChange(index, "nameAr", e.target.value)}
                      className="arabic-field"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.descriptionEn")}
                    name={[index, "descriptionEn"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <Input.TextArea
                      rows={3}
                      onChange={(e) => handleFormChange(index, "descriptionEn", e.target.value)}
                      className="english-field"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.descriptionAr")}
                    name={[index, "descriptionAr"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <Input.TextArea
                      rows={3}
                      onChange={(e) => handleFormChange(index, "descriptionAr", e.target.value)}
                      className="arabic-field"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.price")}
                    name={[index, "price"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(value) => handleFormChange(index, "price", value)}
                      step={0.01} // Allows stepping by 0.01
                      precision={2} // Limits to 2 decimal places
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    label={
                      <>
                        {t("dashboard.landingPageSettings.productsSection.picture")}
                        {"id" in inputFields[index] && (
                          <>
                            &nbsp;
                            <a href={data[index].picture} target="_blank" rel="noopener noreferrer">
                              (
                              {t(
                                "dashboard.landingPageSettings.productsSection.viewCurrentPicture",
                              )}
                              )
                            </a>
                          </>
                        )}
                      </>
                    }
                    name={[index, "picture"]}
                    rules={[
                      {
                        required: !("id" in inputFields[index]),
                        message: t("auth.signUp.requiredFieldMessage"),
                      },
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      ({ getFieldValue }) => ({
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        validator(_, value) {
                          const pictureFile = inputFields[index].picture;

                          // Validate file size (1 MB)
                          if (pictureFile && pictureFile.size > 1024 * 1024) {
                            return Promise.reject(
                              new Error(t("registrationForm.stepTwo.helpText.fileSize")),
                            );
                          }

                          // Validate square image
                          if (pictureFile) {
                            return new Promise((resolve, reject) => {
                              const img = new Image();
                              img.src = URL.createObjectURL(pictureFile);

                              img.onload = () => {
                                if (img.width !== img.height)
                                  reject(
                                    new Error(
                                      t(
                                        "dashboard.landingPageSettings.pictureAlbumSection.squareImage",
                                      ),
                                    ),
                                  );
                                else resolve(); // Validation passed
                              };

                              // if the image cannot be loaded successfully (ex: invalid format)
                              img.onerror = () => {
                                reject(
                                  new Error(
                                    t(
                                      "dashboard.landingPageSettings.pictureAlbumSection.invalidImage",
                                    ),
                                  ),
                                );
                              };
                            });
                          }

                          // Return resolved promise for cases where no file is uploaded
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        handleFormChange(index, "picture", event.target.files[0])
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]} className="mt-3">
                <Col xs={24}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.active")}
                    name={[index, "active"]}
                  >
                    <Checkbox
                      checked={inputFields[index].active}
                      onChange={(e) => handleFormChange(index, "active", e.target.checked)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]} className="mt-3">
                <Col xs={24} lg={12}>
                  <Form.Item
                    label={t("dashboard.landingPageSettings.productsSection.category")}
                    name={[index, "optionCategory"]}
                    rules={[{ required: true, message: t("requestForm.requiredFieldMessage") }]}
                  >
                    <Select onChange={(value) => handleFormChange(index, "optionCategory", value)}>
                      <Option key="" value="">
                        -----
                      </Option>

                      {categoriesListData.categoriesList.map((obj) => (
                        <Option key={obj.id} value={obj.id}>
                          {obj["name" + (currentLanguage === "en" ? "En" : "Ar")]}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {inputFields[index].options?.map((option, optionIdx) => (
                <Card
                  key={optionIdx}
                  type="inner"
                  className="mb-2"
                  extra={
                    option.id ? null : (
                      <DeleteFilled onClick={() => removeOption(index, optionIdx)} />
                    )
                  }
                >
                  <Row gutter={[8, 8]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label={t("dashboard.landingPageSettings.productsSection.valueEn")}
                        required
                        name={[index, "options", optionIdx, "valueEn"]}
                      >
                        <Input
                          value={option.valueEn}
                          onChange={(e) =>
                            handleOptionChange(index, optionIdx, "valueEn", e.target.value)
                          }
                          className="english-field"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label={t("dashboard.landingPageSettings.productsSection.valueAr")}
                        required
                        name={[index, "options", optionIdx, "valueAr"]}
                      >
                        <Input
                          value={option.valueAr}
                          onChange={(e) =>
                            handleOptionChange(index, optionIdx, "valueAr", e.target.value)
                          }
                          className="arabic-field"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label={t("dashboard.landingPageSettings.productsSection.availableQuantity")}
                        required
                        name={[index, "options", optionIdx, "availableQuantity"]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          value={option.availableQuantity}
                          onChange={(val) =>
                            handleOptionChange(index, optionIdx, "availableQuantity", val)
                          }
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Row>
                <Col xs={24}>
                  <Button type="dashed" onClick={() => addOption(index)} block>
                    + {t("dashboard.landingPageSettings.productsSection.addOption")}
                  </Button>
                </Col>
              </Row>
            </Card>

            {index === fields.length - 1 && (
              <Row gutter={[8, 8]} justify="center">
                <Col xs={24} className="mb-4">
                  <Button
                    onClick={() => {
                      addFields();
                      add();
                    }}
                    style={{ width: "100%" }}
                    icon={<PlusOutlined />}
                  />
                </Col>
              </Row>
            )}
          </React.Fragment>
        ))
      }
    </Form.List>
  );
};

export default ProductsFormList;

const StyledButton = styled(Button)`
  svg {
    margin: 0;
  }
`;
